<template>
  <div class="map-wrapper">
    <div class="leaflet-map">
      <div ref="mapContainer" id="map"></div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, watch} from "vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import usePlaceSizes from "@/composables/usePlaceSizes";
import {RVRMarker} from "@/icons/RVRMarker";
import usePlaces from "@/composables/usePlaces";
import useTokenManager from "@/composables/useTokenManager";
import useSettings from "@/composables/useSettings";
import useReservations from "@/composables/useReservations";
import useTimeExpired from "@/composables/useTimeExpired";
import { MAP_IMAGE_VERSION } from "@/composables/useUtilityFunctions";

import { apiCreateReservation, apiDeleteReservation } from "@/api/endpoints";
import useMessages from "@/composables/useMessages";
import useStepNavigation from "@/composables/useStepNavigation";
import useSpinner from "@/composables/useSpinner";
import useTexts from "@/composables/useTexts";
import {useFormatPrice} from "@/composables/useUtilityFunctions";


export default {
  name: "LeafletMapPublic",
  setup() {
    const { reservations, nrOfReservations, addReservation, deleteReservation, clearReservations } = useReservations();
    const { placeSizes } = usePlaceSizes();
    const { places, fetchPlaces, placesLoaded } = usePlaces();
    const { settings } = useSettings();
    const { token } = useTokenManager();
    const { timeExpired } = useTimeExpired();
    const { showMessage } = useMessages();
    const { nextButtonPressed} = useStepNavigation();
    const { showSpinner, hideSpinner} = useSpinner();
    const { getTextsForComponent, replacePlaceholders } = useTexts();
    const { formatPrice } = useFormatPrice()
    const texts = getTextsForComponent('LeafletMapPublic');

    const mapContainer = ref(null);
    const markers = ref([]);

    let map = null;
    let currentPopup = null;
    let currentMarker = ref(null);
    const defaultMarkerColor = '#898989';

    onMounted(async () => {
      const maxBounds = [
        [-150, -150],
        [3045, 3650],
      ];

      const mapBounds = [
        [0, 0],
        [2895, 3500],
      ];

      map = L.map(mapContainer.value, {
        crs: L.CRS.Simple,
        minZoom: -3,
        maxZoom: 2,
        zoomSnap: 0.25,
        maxBounds: maxBounds,
        maxBoundsViscosity: 0.5,
      }).fitBounds(mapBounds);

      L.imageOverlay(process.env.PLUGIN_BASE_URL + "images/festival_ground_map.jpg?v=" + MAP_IMAGE_VERSION, mapBounds).addTo(map);

     map.setView([1447.5, 1750], -1.45);

      map.on('click', () => {
        if (currentPopup) {
          map.closePopup(currentPopup); // Close the current popup
          currentPopup = null;
        }
      });

      map.on('mousemove', (e) => {
        if (currentMarker.value && !currentMarker.value._icon.contains(e.originalEvent.target)) {
          currentMarker.value.setIcon(currentMarker.value.options.normalIcon);
          currentMarker.value = null;
        }
      });

      if(timeExpired.value) {
        await handleTimeExpired()
      }

      placeMarkersOnMap();
    });

    watch(timeExpired, (newVal) => {
      if(newVal) {
        handleTimeExpired();
      }
    });

    const placeMarkersOnMap = () => {

      // Add each place as a marker on the map
      places.value.forEach(place => {
        addMarker(place);
        if (isCurrentUsersPlace(place)) {
          handleNewReservation(place);
        }
      });
    }
     const removeAllMarkers = () => {
       markers.value.forEach(marker => marker.remove());
     }

    const getDataForPlaceSizeID = (sizeID) => {
      const placeSize = placeSizes.value.find(placeSize => placeSize.id === sizeID);
      if (!placeSize) {
        return {
          placeColor: defaultMarkerColor,
          placePrice: formatPrice(0),
          placeSize: null,
        };
      }
      return {
        placeColor: placeSize.color_code,
        placePrice: formatPrice(parseFloat(placeSize.price)),
        placeSize: placeSize.size, // Assuming `size` is the name of the size element in your `placeSize` object
      };
    };


    const isCurrentUsersPlace = (place) => {
      return place.temporary_user_token === token.value;
    }

    const handleNewReservation = (place) => {
      addReservation(place.markerId, { placeSizeId: place.placeSizeId });
      const color = '#40A070';
      setMarkerIcon(place, color);
    }

    const handleReservationDeletion = (place) => {
      deleteReservation(place.markerId);
      const color = place.color;
      setMarkerIcon(place, color);
    }

    const setMarkerIcon = (place, color) => {
      const markerIndex = findMarkerIndex(place.markerId);
      const newIcon = RVRMarker(color, place.markerId);
      const newEnlargedIcon = RVRMarker(color, place.markerId, [40, 60]);
      markers.value[markerIndex].setIcon(newIcon);
      markers.value[markerIndex].options.normalIcon = newIcon;
      markers.value[markerIndex].options.enlargedIcon = newEnlargedIcon;
    }

    const addMarker = (place) => {
      const latlng = L.latLng(place.lat, place.lng);
      let color;

      const { placeColor, placePrice, placeSize } = getDataForPlaceSizeID(place.placeSizeId);

      place.price = placePrice;
      place.size = placeSize;
      place.color = placeColor;

      // Switch statement to determine the color based on the place's status
      switch (place.status) {
        case 'permanent':
        case 'pending':
          color = '#898989';
          break;
        case 'temporary':
          color = '#C0C0C0';
          break;
        default:
          color = placeColor;
      }

      const normalIcon = RVRMarker(color, place.markerId);
      const enlargedIcon = RVRMarker(color, place.markerId, [40, 64]);

      const marker = L.marker(latlng, {
        icon: normalIcon,
      }).addTo(map);

      marker.options.markerId = place.markerId;
      marker.options.placeSizeId = place.placeSizeId;
      marker.options.temporary_user_token = place.temporary_user_token;
      marker.options.normalIcon = normalIcon;
      marker.options.enlargedIcon = enlargedIcon;

      marker.on('mouseover', () => {
        if (currentMarker.value && currentMarker.value !== marker) {
          currentMarker.value.setIcon(currentMarker.value.options.normalIcon);
        }
        marker.setIcon(marker.options.enlargedIcon);
        currentMarker.value = marker;
      });


      marker.on('click', () => {
        if (currentPopup) {
          map.closePopup(currentPopup); // Close the current popup
          currentPopup = null;
        }

        marker.unbindPopup();
        const form = createPopupContent(place, marker, texts.value);

        const popup = L.popup({
          _noMoveStart: true,
          autoClose: false,
          closeOnClick: false,
          closeButton: false
        }).setContent(form);

        marker.bindPopup(popup);
        marker.openPopup();

        currentPopup = popup;
      });

      markers.value.push(marker);

      return marker;
    };

    const findMarkerIndex = (markerId) => {
      return markers.value.findIndex(marker => marker.options.markerId === markerId);
    };

    const createPopupContent = (place, marker, t) => {
      let form;

      switch(place.status) {
        case 'temporary':
        case 'pending':
          form = document.createElement("form");

          if(isCurrentUsersPlace(place)) {
            form.innerHTML = `
            <h2>${t.place} ${place.markerId}</h2>
            <span style="background-color: ${place.color}; width: 20px; height: 20px; display: inline-block; margin-right: 5px; border-radius: 4px;"></span>
             <span style="vertical-align: middle; padding-bottom: 12px;">${place.size}</span>
             <span style="vertical-align: middle; padding-bottom: 12px; font-style: italic; font-weight: bold;">&nbsp; ${place.price}</span>
            <p style="margin: 7px 0 15px;">${t.place_reserved_for_you}</p>
            <button type="button" id="deleteReservationButton">${t.delete_reservation_button}</button>
            <button type="button" id="dismissButton">${t.cancel_button}</button>
            `;

            form.querySelector('#deleteReservationButton').addEventListener("click", async (event) => {
              // disable the button immediately after it is clicked
              event.target.disabled = true;

              // Prepare the newReservation object
              const reservationToDelete = {
                markerId: place.markerId,
              };

              try {
                showSpinner();
                await apiDeleteReservation(reservationToDelete)

                marker.closePopup();
                currentPopup = null;

                place.status = null;
                place.temporary_user_token = null;

                handleReservationDeletion(place);
                hideSpinner();

              } catch (error) {
                console.error('There was an error deleting the reservation:', error);
              }
            })
          }
          else {
            form.innerHTML = `
            <h2>${t.place} ${place.markerId}</h2>
            <p>${t.temporarily_reserved}</p>
            <button type="button" id="dismissButton">${t.close_button}</button>
            `;
          }
          break;
        case 'permanent':
          form = document.createElement("form");
          form.innerHTML = `
            <h2>${t.place} ${place.markerId}</h2>
            <p>${replacePlaceholders(t.permanently_reserved, { companyname: place.companyname, cityname: place.cityname })}</p>
            <button type="button" id="dismissButton">${t.close_button}</button>
            `;
          break;
        default:
          form = document.createElement("form");
          form.innerHTML = `
            <h2>${t.place} ${place.markerId}</h2>
            <span style="background-color: ${place.color}; width: 20px; height: 20px; display: inline-block; margin-right: 5px; border-radius: 4px;"></span>
            <span style="vertical-align: middle; padding-bottom: 12px;">${place.size}</span>
            <span style="vertical-align: middle; padding-bottom: 12px; font-style: italic; font-weight: bold;">&nbsp; ${place.price}</span>
            <p style="margin: 7px 0 15px;">${t.reserve_this_place}</p>
            <button type="button" id="reservePlaceButton">${t.reserve}</button>
            <button type="button" id="dismissButton">${t.cancel_button}</button>
            `;

          form.querySelector('#reservePlaceButton').addEventListener("click", async (event) => {
            // disable the button immediately after it is clicked
            event.target.disabled = true;

            // Prepare the newReservation object
            const newReservation = {
              markerId: place.markerId,
              status: 'temporary',
            };

            try {
              showSpinner();
              await apiCreateReservation(newReservation);
              marker.closePopup();
              currentPopup = null;

              place.status = 'temporary';
              place.temporary_user_token = token.value;

              handleNewReservation(place);
              hideSpinner();
            }
            catch (error) {
              if (error.response && error.response.status === 409) {
                // Specific handling for 409 Conflict (reservation not possible because other user has already reserved it)
                showMessage('Deze plaats is helaas zojuist al gereserveerd door een andere standhouder, excuses voor het ongemak!');
                marker.closePopup();
                currentPopup = null;
                hideSpinner();

              } else {
                // General error handling
                console.error('There was an error creating the place:', error);
              }
            }

          })
          break;
      }

      form.querySelector("#dismissButton").addEventListener("click", () => {
        marker.closePopup();
        currentPopup = null;
      });

      return form;
    };

    const handleTimeExpired = async () => {
      //Iterate over the object of reserved places
      for (let reservationMarkerId of Object.keys(reservations)) {
        // Prepare the reservationToDelete object
        const reservationToDelete = {
          markerId: reservationMarkerId,
        };

        try {
          await apiDeleteReservation(reservationToDelete)
        } catch (error) {
          console.error('There was an error deleting the reservation:', error);
        }
      }
      clearReservations();

      //When the time expired we begin with a clean slate
      //Remove all markers, and reload the places from the server
      removeAllMarkers();

      placesLoaded.value = false;
      timeExpired.value = false;

      await fetchPlaces();
    }

    return {
      map,
      mapContainer,
      handleTimeExpired,
      timeExpired,
      settings,
      token,
      nrOfReservations,
      currentPopup,
      reservations,
      nextButtonPressed,
      texts,
      places,
      getDataForPlaceSizeID
    };
  },
};
</script>

<style scoped>
.map-wrapper {
  width: 100%;
  max-width: 100%;
  position: relative;
}

.leaflet-map {
  width: 100%;
  height: 0;
  padding-bottom: 82.71%; /* matches image aspect ratio (2895/3500 * 100) */
  position: relative;
}
#map {width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  background: #218c7c;
}

</style>
